import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["notificationPanel", "badge"]
  static values = {
    unreadCount: Number,
    autoMarkOnOpen: { type: Boolean, default: false }
  }

  connect() {
    this._onCableEvent = (e) => this.onNotificationReceived(e.detail)
    window.addEventListener("notifications:received", this._onCableEvent)
    this.refreshBadge()
  }

  disconnect() {
    window.removeEventListener("notifications:received", this._onCableEvent)
  }

  // ------ UI helpers
  refreshBadge() {
    const count = this.unreadCountValue || 0
    if (!this.hasBadgeTarget) return
    this.badgeTarget.textContent = String(count)
    this.badgeTarget.classList.toggle("hidden", count === 0)
  }

  incrementBadge() {
    this.unreadCountValue = (this.unreadCountValue || 0) + 1
    this.refreshBadge()
  }

  decrementBadge() {
    const next = Math.max(0, (this.unreadCountValue || 0) - 1)
    this.unreadCountValue = next
    this.refreshBadge()
  }

  csrfToken() {
    const el = document.querySelector('meta[name="csrf-token"]')
    return el ? el.content : ""
  }

  removeListItem(li) {
    if (!li) return
    li.style.transition = "opacity 120ms ease, transform 120ms ease"
    li.style.opacity = "0"
    li.style.transform = "translateY(-2px)"
    setTimeout(() => {
      li.remove()
      const list = document.querySelector("[data-notifications-list]")
      if (list && list.children.length === 0) {
        list.innerHTML = '<li data-notifications-empty class="px-4 py-6 text-center text-sm text-slate-500">No new notifications</li>'
      }
    }, 130)
  }

  // ------ Events from ActionCable
  onNotificationReceived(data) {
    this.incrementBadge()
    const list = document.querySelector("[data-notifications-list]")
    const empty = document.querySelector("[data-notifications-empty]")
    if (empty) empty.remove()
    if (list) {
      const li = document.createElement("li")
      li.className = "group"
      li.innerHTML = `
        <div class="relative">
          <a href="${this.escapeHtml(data.url || "#")}" class="block px-4 py-3 pr-12 transition rounded-lg hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50">
            <div class="flex items-start gap-3">
              <span class="mt-1 inline-block size-2 rounded-full bg-blue-600 group-hover:scale-110 transition" data-unread-dot></span>
              <div class="min-w-0 flex-1">
                <div class="flex items-baseline justify-between gap-3">
                  <p class="truncate text-sm font-medium text-slate-900">${this.escapeHtml(data.title || "Notification")}</p>
                  <time class="shrink-0 text-xs text-slate-500">${new Date(data.created_at).toLocaleTimeString()}</time>
                </div>
                <p class="mt-1 text-sm text-slate-600 line-clamp-2">${this.escapeHtml(data.body || "")}</p>
              </div>
            </div>
          </a>
          <button
            type="button"
            aria-label="Dismiss notification"
            title="Dismiss"
            class="absolute top-1.5 right-2 inline-flex size-7 items-center justify-center rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
            data-action="click->notifications#clearOne"
            data-url="${this.escapeHtml(data.destroy_url || "/admin/notifications/0")}"
            data-unread="true">
            &times;
          </button>
        </div>
      `
      list.prepend(li)
    }
  }

  escapeHtml(str) {
    return (str || "").toString()
      .replaceAll("&","&amp;").replaceAll("<","&lt;")
      .replaceAll(">","&gt;").replaceAll('"',"&quot;").replaceAll("'","&#039;")
  }

  // ------ UI actions
  async toggleNotifications() {
    if (!this.hasNotificationPanelTarget) return
    const opening = this.notificationPanelTarget.classList.contains("hidden")
    this.notificationPanelTarget.classList.toggle("hidden")
    if (opening && this.autoMarkOnOpenValue) {
      await this.markAllRead()
    }
  }

  async markAllRead(event) {
    if (event) event.preventDefault()
    await fetch("/admin/notifications/mark_all_read", {
      method: "POST",
      headers: { "X-CSRF-Token": this.csrfToken() }
    })
    this.unreadCountValue = 0
    this.refreshBadge()
    document.querySelectorAll("[data-unread-dot]").forEach(dot => dot.classList.remove("bg-blue-600"))
  }

  async clearAll(event) {
    if (event) event.preventDefault()
    await fetch("/admin/notifications/clear_all", {
      method: "DELETE",
      headers: { "X-CSRF-Token": this.csrfToken() }
    })
    const list = document.querySelector("[data-notifications-list]")
    if (list) {
      list.innerHTML = '<li data-notifications-empty class="px-4 py-6 text-center text-sm text-slate-500">No new notifications</li>'
    }
    this.unreadCountValue = 0
    this.refreshBadge()
  }

  async clearOne(event) {
    event.preventDefault()
    const btn = event.currentTarget
    const url = btn.dataset.url
    const li = btn.closest("li")
    const unread = btn.dataset.unread === "true"

    if (!url || !li) return

    try {
      const res = await fetch(url, {
        method: "DELETE",
        headers: { "X-CSRF-Token": this.csrfToken() }
      })
      if (!res.ok) throw new Error(`Failed ${res.status}`)
      this.removeListItem(li)
      if (unread) this.decrementBadge()
    } catch (e) {
      console.error(e)
      // soft-fallback: still remove from UI
      this.removeListItem(li)
    }
  }
}
